import React from 'react';
import { Check, Clock, Repeat2 } from 'lucide-react';
import { buildRoleSwitchItems, type SwitchableRoleOption } from '../../lib/roleSwitch';
import { useReferrerJourney } from '../../hooks/useReferrerJourney';

interface RoleSwitcherSectionProps {
  /** The user's role options (server-truth list from the role resolver). */
  options: SwitchableRoleOption[];
  /** The role currently being viewed — marked, not clickable. */
  currentRoleId: string;
  /** Switch to the chosen role (validated by the caller; ends in a reload). */
  onSelectRole: (roleId: string) => void;
  /**
   * Role data still resolving: render an explicit "Loading your roles…"
   * row instead of nothing — role surfaces never render blank (G15/G17).
   */
  loading?: boolean;
}

/**
 * The role switcher — lives ONLY inside the profile (avatar) dropdown,
 * identically on every portal. Lists the user's roles with the current
 * one marked; the referrer row shows a "Pending" badge while its
 * application awaits an admin decision (server truth via the shared
 * journey hook — the same source the dashboard card reads, so the two
 * can never disagree). Selecting a role validates server-side and then
 * HARD-RELOADS to that role's dashboard (or its Pending Approval gate
 * when pending) — the app boots fresh from server truth.
 *
 * Renders nothing for single-role users once loaded; while loading it
 * shows an explicit "Loading your roles…" row (never a blank gap).
 */
export default function RoleSwitcherSection({ options, currentRoleId, onSelectRole, loading }: RoleSwitcherSectionProps) {
  const { doc } = useReferrerJourney();
  if (loading) {
    return (
      <div className="px-2 py-2 border-b border-slate-100" role="status" aria-live="polite">
        <p className="flex items-center gap-1.5 px-2 pb-1.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
          <Repeat2 className="h-3 w-3" aria-hidden="true" />
          Switch role
        </p>
        <p className="px-2 py-1 text-sm text-slate-500">Loading your roles…</p>
      </div>
    );
  }
  if (!options || options.length <= 1) return null;
  const items = buildRoleSwitchItems(options, currentRoleId, doc?.status);

  return (
    <div className="px-2 py-2 border-b border-slate-100">
      <p className="flex items-center gap-1.5 px-2 pb-1.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
        <Repeat2 className="h-3 w-3" aria-hidden="true" />
        Switch role
      </p>
      <div className="space-y-0.5">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            disabled={item.isCurrent}
            onClick={() => { if (!item.isCurrent) onSelectRole(item.id); }}
            aria-current={item.isCurrent ? 'true' : undefined}
            className={`w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-xs font-bold transition ${
              item.isCurrent
                ? 'bg-brand/5 text-slate-900 cursor-default'
                : 'text-slate-600 hover:bg-slate-50 hover:text-brand cursor-pointer'
            }`}
          >
            <span className="truncate">{item.title}</span>
            {item.isCurrent ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-extrabold text-brand">
                <Check className="h-3 w-3" aria-hidden="true" />
                Current
              </span>
            ) : item.pendingBadge ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-extrabold text-amber-700">
                <Clock className="h-3 w-3" aria-hidden="true" />
                Pending
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}
